import React, { useState, useEffect } from 'react';
import microbiologyService from '../../services/microbiologyService';
import './MicrobiologyWorkbench.css';

export default function AntibiogramPage() {
  const [data, setData] = useState([]);
  const [specimens, setSpecimens] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedSpecimen, setSelectedSpecimen] = useState('');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const loadAntibiogram = async () => {
    try {
      setLoading(true);
      const [abRes, spRes] = await Promise.all([
        microbiologyService.getAntibiogram({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          specimenId: selectedSpecimen || undefined
        }),
        microbiologyService.getSpecimens()
      ]);
      setData(abRes?.data || []);
      setSpecimens(spRes?.data || []);
    } catch (err) {
      console.error('Error loading antibiogram:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAntibiogram();
  }, [dateRange, selectedSpecimen]);

  // Pivot data: Rows = Organisms, Columns = Antimicrobials
  const organisms = Array.from(new Set(data.map((d) => d.organismName)));
  const antimicrobials = Array.from(new Set(data.map((d) => `${d.drugName} (${d.drugCode})`)));

  const getCellData = (orgName, drugLabel) => {
    return data.find((d) => d.organismName === orgName && `${d.drugName} (${d.drugCode})` === drugLabel);
  };

  const getSusceptibilityColor = (pct) => {
    if (pct >= 80) return { bg: '#dcfce7', text: '#15803d' };
    if (pct >= 50) return { bg: '#fef9c3', text: '#854d0e' };
    return { bg: '#fee2e2', text: '#b91c1c' };
  };

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.2">
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </svg>
            CLSI M39 Cumulative Antibiogram & AMR Surveillance
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            Aggregated population antimicrobial susceptibility rates and epidemiological resistance heatmaps
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="micro-filter-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: '600', color: '#475569' }}>From Date:</label>
          <input
            type="date"
            className="micro-filter-select"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: '600', color: '#475569' }}>To Date:</label>
          <input
            type="date"
            className="micro-filter-select"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
          />
        </div>

        <select
          className="micro-filter-select"
          value={selectedSpecimen}
          onChange={(e) => setSelectedSpecimen(e.target.value)}
        >
          <option value="">All Specimen Matrices</option>
          {specimens.map((sp) => (
            <option key={sp.specimenID} value={sp.specimenID}>
              {sp.specimenName}
            </option>
          ))}
        </select>
      </div>

      {/* Cross-Tabular Antibiogram Matrix */}
      <div className="micro-table-wrapper" style={{ overflowX: 'auto' }}>
        <table className="micro-table" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ minWidth: '220px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2 }}>
                Microorganism Name
              </th>
              {antimicrobials.map((drug) => (
                <th key={drug} style={{ textAlign: 'center', minWidth: '130px', fontSize: '0.72rem' }}>
                  {drug}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={antimicrobials.length + 1} style={{ textAlign: 'center', padding: '36px' }}>
                  Calculating cumulative population antibiogram...
                </td>
              </tr>
            ) : organisms.length === 0 ? (
              <tr>
                <td colSpan={antimicrobials.length + 1} style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                  No AST susceptibility records found for this period. As culture results are released in the workbench, they will populate here.
                </td>
              </tr>
            ) : (
              organisms.map((org) => (
                <tr key={org}>
                  <td style={{ position: 'sticky', left: 0, background: '#fff', fontWeight: '600', zIndex: 1 }}>
                    <em>{org}</em>
                  </td>
                  {antimicrobials.map((drug) => {
                    const cell = getCellData(org, drug);
                    if (!cell || cell.totalTested === 0) {
                      return (
                        <td key={drug} style={{ textAlign: 'center', color: '#cbd5e1', fontSize: '0.8rem' }}>
                          —
                        </td>
                      );
                    }
                    const { bg, text } = getSusceptibilityColor(cell.percentSusceptible);
                    return (
                      <td
                        key={drug}
                        style={{
                          textAlign: 'center',
                          background: bg,
                          color: text,
                          fontWeight: '700',
                          padding: '10px 6px'
                        }}
                      >
                        <div style={{ fontSize: '0.9rem' }}>{cell.percentSusceptible}%</div>
                        <div style={{ fontSize: '0.68rem', fontWeight: 'normal', color: '#475569' }}>
                          ({cell.sensitiveCount}/{cell.totalTested})
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
